import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';
import i18n from '@/i18n/config';

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: ['2xs', 'micro', 'label', 'input-lg'] }],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Returns date as DD.MM.YY (e.g. 19.02.26) — consistent across all locales */
function toNumericDate(d: Date): string {
  if (isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = String(d.getFullYear()).slice(-2);
  return `${day}.${month}.${year}`;
}

export function formatDate(date: string | Date): string {
  return toNumericDate(new Date(date));
}

/** Formats a number with thousands separators per the active UI language (not OS locale). */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat(resolveLocale()).format(value);
}

export function resolveLocale(): string {
  const language = i18n.language || 'en';
  const localeMap: Record<string, string> = {
    en: 'en-US',
    ru: 'ru-RU',
    uk: 'uk-UA',
    es: 'es-ES',
  };
  return localeMap[language] ?? language;
}

/**
 * `Intl.DateTimeFormat` строится один раз на (локаль + набор полей).
 *
 * Конструктор — дорогая часть Intl (разбор локали, сборка правил); `format()`
 * дёшев. Пока форматтер собирался на каждый вызов, подпись двух отметок
 * времени в списке записей стоила по два конструктора НА СТРОКУ — до сорока на
 * рендер страницы в двадцать строк (#359).
 *
 * Ключ включает локаль, поэтому переключение языка не отдаёт старый форматтер.
 * Кеш модульный: локалей четыре, наборов полей — единицы, расти ему некуда.
 */
const dateTimeFormatters = new Map<string, Intl.DateTimeFormat>();

export function dateTimeFormatter(
  options: Intl.DateTimeFormatOptions,
  locale: string = resolveLocale()
): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let formatter = dateTimeFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options);
    dateTimeFormatters.set(key, formatter);
  }
  return formatter;
}

const EN_WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const EN_MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * Localizes a `YYYY-MM-DD` day as month + day ("Mar 9"), in UTC.
 *
 * Used wherever a tick must carry a calendar anchor regardless of window width
 * — e.g. a weekly bucket, which a bare weekday could never identify. Returns
 * the input unchanged if it isn't a date-only string.
 */
export function localizeMonthDay(isoDay: string): string {
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDay);
  if (!iso) return isoDay;
  const date = new Date(Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])));
  return new Intl.DateTimeFormat(resolveLocale(), {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

/**
 * Localizes chart x-axis day labels.
 *
 * `label` is usually a raw `YYYY-MM-DD` isoDate (recordsChart/revenueChart,
 * getDaysBetween — WS1) — the server only buckets by calendar day, never
 * formats a display label, so localization belongs here: a short weekday
 * (unambiguous within a `windowDays <= 7` selection) or month+day (longer
 * windows, where a bare weekday would repeat). `windowDays` defaults to a
 * <=7 window when the caller doesn't pass the chart's actual length.
 *
 * Two legacy label forms are also recognized for safety: the pre-WS1 backend
 * shape (`Mon`..`Sun` / `Jun 22`) and the client-generated `Day N` synthetic
 * label (e.g. SupervisorDashboard's index-only qualityTrends sparkline).
 * Unrecognized labels pass through unchanged.
 */
export function localizeChartDayLabel(
  label: string,
  t: (key: string, defaultValue: string, options?: Record<string, unknown>) => string,
  windowDays: number = 7
): string {
  const locale = resolveLocale();

  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(label);
  if (iso) {
    if (windowDays > 7) return localizeMonthDay(label);
    const date = new Date(Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])));
    return new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(
      date
    );
  }

  const wd = EN_WEEKDAYS_SHORT.indexOf(label as (typeof EN_WEEKDAYS_SHORT)[number]);
  if (wd >= 0) {
    // 2023-01-01 (UTC) is a Sunday; offset by weekday index.
    const date = new Date(Date.UTC(2023, 0, 1 + wd));
    return new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(
      date
    );
  }

  const monthDay = /^([A-Za-z]{3}) (\d{1,2})$/.exec(label);
  if (monthDay) {
    const mi = EN_MONTHS_SHORT.indexOf(monthDay[1] as (typeof EN_MONTHS_SHORT)[number]);
    if (mi >= 0) {
      const date = new Date(Date.UTC(2023, mi, 1));
      const month = new Intl.DateTimeFormat(locale, {
        month: 'short',
        timeZone: 'UTC',
      }).format(date);
      return `${month} ${monthDay[2]}`;
    }
  }

  const dayN = /^Day (\d+)$/.exec(label);
  if (dayN) {
    return t('common:periodNav.dayN', 'Day {{n}}', { n: Number(dayN[1]) });
  }

  return label;
}

export function formatDateShort(date: string | Date): string {
  return toNumericDate(new Date(date));
}

export function formatDateTime(date: string | Date): string {
  const d = new Date(date);
  if (isNaN(d.getTime())) return '—';
  const locale = resolveLocale();
  const time = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
  return `${toNumericDate(d)}, ${time}`;
}

/**
 * Дата и время в локали интерфейса, коротким месяцем: «19 июн. в 16:28».
 *
 * Отличается от `formatDateTime` тем, что не собирает числовую дату, а даёт
 * `Intl` выбрать порядок и разделители по активной локали (AU-7). Живёт здесь,
 * а не внутри экрана записи, потому что этим форматом обязаны пользоваться и
 * сама карточка, и её подкомпоненты: пока формат был локальной функцией одного
 * файла, вынесенный из него блок отметок времени начал печатать «19.06.26,
 * 16:28» рядом с «19 июн. в 16:28» — два вида одного момента в одной карточке.
 */
export function formatDateTimeLocaleShort(
  date: string | Date | null | undefined
): string {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  return dateTimeFormatter({
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

/**
 * Подсказка «Звонок … · Принято …» для мест, где на экране помещается только
 * одна отметка (относительное время в компактных таблицах).
 *
 * Живёт рядом с форматтерами, а не копируется по экранам: тот же текст нужен
 * встроенному дашборду и списку пропущенных, и три ручные сборки уже начали
 * расходиться — где-то `t(key, 'Call')`, где-то `t(key, {defaultValue})`, а
 * задержку показывала только карточка.
 */
export function buildTimestampsTooltip(
  eventTime: string | null | undefined,
  createdAt: string | null | undefined,
  labels: { call: string; received: string }
): string {
  return [
    eventTime && `${labels.call} ${formatDateTimeLocaleShort(eventTime)}`,
    createdAt && `${labels.received} ${formatDateTimeLocaleShort(createdAt)}`,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Дата в локали интерфейса: «Aug 22, 2026», «22 серп. 2026 р.» (#1101).
 *
 * В отличие от `formatDate` (всегда DD.MM.YY) порядок, месяц и год выбирает
 * `Intl` по языку интерфейса — для дат, которые читают вне таблиц: продление
 * тарифа, срок понижения, дата запроса субъекта данных. Англоязычный читатель
 * прочитал бы «22.08.26» как 22-й месяц.
 */
export function formatDateLocale(date: string | Date): string {
  const d = new Date(date);
  if (isNaN(d.getTime())) return '—';
  return dateTimeFormatter({ day: 'numeric', month: 'short', year: 'numeric' }).format(d);
}

/**
 * Дата и время с годом в локали интерфейса: «Sep 26, 2026, 02:05 PM»,
 * «26 вер. 2026 р., 14:05». Для отметок, которые могут быть старше года
 * (запуск пересборки индекса, события алертов), — без года декабрьская
 * отметка, прочитанная в январе, неоднозначна.
 */
export function formatDateTimeLocale(date: string | Date): string {
  const d = new Date(date);
  if (isNaN(d.getTime())) return '—';
  return dateTimeFormatter({
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

/** Один форматтер смещения на зону: конструктор Intl дорог (#359), а список зон перерисовывается на каждый ввод. */
const utcOffsetFormatters = new Map<string, Intl.DateTimeFormat | null>();

/**
 * Смещение часового пояса на дату `at`: «UTC+3», «UTC+5:30», «UTC-5» (#1102).
 *
 * Смещение считается, а не пишется в текст: у зон с летним временем оно
 * меняется дважды в год, и зашитое «Київ (UTC+2)» полгода обещало сдвиг на
 * час тому, кто сверяет по нему время звонков.
 *
 * `null` — движок не знает `timeZoneName: 'shortOffset'` (Safari < 15.4,
 * Chromium < 95) и бросает `RangeError`: подпись тогда остаётся без смещения,
 * а не роняет вкладку настроек целиком.
 */
export function formatUtcOffset(timeZone: string, at: Date = new Date()): string | null {
  if (!utcOffsetFormatters.has(timeZone)) {
    let formatter: Intl.DateTimeFormat | null;
    try {
      formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'shortOffset',
      });
    } catch {
      // Remembered: an engine that refuses once refuses every time, and the
      // list is rebuilt on renders — don't throw thirteen times a keystroke.
      formatter = null;
    }
    utcOffsetFormatters.set(timeZone, formatter);
  }
  const formatter = utcOffsetFormatters.get(timeZone);
  if (!formatter) return null;
  const zoneName = formatter
    .formatToParts(at)
    .find((part) => part.type === 'timeZoneName')?.value;
  const offset = (zoneName ?? 'GMT').replace(/^GMT/, '');
  return `UTC${offset || '+0'}`;
}

export function formatTime(date: string | Date): string {
  const d = new Date(date);
  if (isNaN(d.getTime())) return '—';
  return dateTimeFormatter({
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(d);
}

/**
 * Short date for charts/tables: "Feb 12" for current year, "Nov 10, 2017" for past years.
 */
export function formatShortDateWithYear(date: string | Date, locale?: string): string {
  const d = new Date(date);
  if (isNaN(d.getTime())) return '—';
  const resolvedLocale = locale ?? resolveLocale();
  const now = new Date();
  if (d.getFullYear() !== now.getFullYear()) {
    return d.toLocaleDateString(resolvedLocale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
  return d.toLocaleDateString(resolvedLocale, { month: 'short', day: 'numeric' });
}

/** Compact time suffixes per locale: m/h/d → м/ч/д (ru), хв/год/дн (uk), etc. */
const COMPACT_UNITS: Record<string, { now: string; m: string; h: string; d: string }> = {
  ru: { now: 'сейчас', m: 'м', h: 'ч', d: 'д' },
  uk: { now: 'зараз', m: 'хв', h: 'год', d: 'д' },
  es: { now: 'ahora', m: 'min', h: 'h', d: 'd' },
  en: { now: 'now', m: 'm', h: 'h', d: 'd' },
};

function getCompactUnits(locale: string): {
  now: string;
  m: string;
  h: string;
  d: string;
} {
  const lang = locale.slice(0, 2);
  return COMPACT_UNITS[lang] ?? COMPACT_UNITS.en!;
}

/**
 * Задержка приёма: сколько прошло между разговором и его появлением в Aelo.
 *
 * Возвращает `null`, когда показывать нечего — и это главное в функции.
 * Величина осмысленна только в узком окне, а за его пределами описывает не
 * задержку, а что-то другое (замеры по проду, август 2026, 17 686 записей):
 *
 * - **меньше 5 минут** — шум: 20% записей приходят так, подпись только мешает;
 * - **больше суток** — это не приём, а импорт истории. Максимум в проде —
 *   14 дней: активность в CRM создана давно, Aelo принял её сегодня, и
 *   «задержка 14 дней» читалась бы как обвинение нам;
 * - **отрицательная** — активность запланирована на будущее, её `START_TIME`
 *   ещё не наступил. За август таких нет, но плановые звонки существуют, и
 *   «принято за −3 дня до звонка» — не то, что стоит рисовать.
 *
 * Отсчёт идёт от КОНЦА разговора (`eventTime` + длительность), когда отметка
 * источника — начало (Bitrix24 `START_TIME`): без поправки получасовой звонок,
 * доставленный мгновенно, показывал «+30м» задержки доставки, которой не было.
 * У Uspacy та же отметка — уже конец разговора; как это различается, разобрано
 * в теле функции.
 *
 * Между 5 минутами и часом лежит 78% записей: приём держится на backfill-кроне
 * (раз в 5 минут), а не на вебхуках — см. #357. Именно эти минуты пользователь
 * и относил на счёт анализа (#359).
 *
 * ВАЖНО про формат: `eventTime` приходит из CRM со смещением портала
 * (`2026-08-21T16:03:54+03:00`), а `createdAt` — в UTC (`…Z`). Сравнивать их
 * как СТРОКИ нельзя: лексикографически такая пара даёт обратный порядок в 99%
 * случаев. `Date` разбирает оба формата и приводит к одной шкале.
 */
export function formatIngestLag(
  eventTime: string | Date | null | undefined,
  createdAt: string | Date | null | undefined,
  durationSeconds?: number | null
): string | null {
  if (!eventTime || !createdAt) return null;

  const event = new Date(eventTime).getTime();
  const received = new Date(createdAt).getTime();
  if (isNaN(event) || isNaN(received)) return null;

  // Отсчёт от КОНЦА разговора — но у разных источников отметка значит разное.
  //
  // Bitrix24 кладёт в неё `START_TIME`, и без поправки на длительность в
  // «задержку» попадал весь разговор: получасовой звонок, доставленный в ту же
  // секунду, что и закончился, показывал «+30м» — подпись обещает задержку
  // ДОСТАВКИ, а называла чужую цифру. Uspacy же кладёт `end_time`
  // (`enrich-uspacy.ts`), и там поправка вычитает длительность второй раз.
  //
  // Схема о поле честна: `version_ts` описан как «provider-side last-change
  // marker», а не время разговора, — единой семантики у него нет и не было.
  // Правильное лечение — донести её от приёма (#424); здесь важно не соврать
  // в обе стороны.
  //
  // Отрицательная поправка — не край, а ДОКАЗАТЕЛЬСТВО: принять запись раньше,
  // чем разговор закончился, невозможно, значит отметка уже была концом, и
  // честная задержка — неисправленная. Это ровно случай Uspacy (десятиминутный
  // звонок, принятый через шесть минут после конца).
  //
  // Там, где вычесть длительность всё-таки можно, остаётся заниженная оценка:
  // занизить безопаснее, чем завысить, — завышенная цифра приписывает нам
  // чужие минуты, а это и есть #359. Длительность неизвестна (часть чатов,
  // записи без метаданных) — поправки нет вовсе.
  const grossMs = received - event;
  const durationMs = Math.max(0, durationSeconds ?? 0) * 1000;
  const correctedMs = grossMs - durationMs;
  const lagMs = correctedMs < 0 ? grossMs : correctedMs;
  const FIVE_MINUTES = 5 * 60 * 1000;
  const ONE_DAY = 24 * 60 * 60 * 1000;
  if (lagMs < FIVE_MINUTES || lagMs > ONE_DAY) return null;

  const units = getCompactUnits(resolveLocale());
  const lagMin = Math.floor(lagMs / 60000);
  return lagMin < 60 ? `${lagMin}${units.m}` : `${Math.floor(lagMin / 60)}${units.h}`;
}

export function formatRelativeTime(date: string | Date, nowMs?: number): string {
  const then = new Date(date).getTime();
  if (isNaN(then)) return '—';
  const locale = resolveLocale();
  const units = getCompactUnits(locale);
  // React callers should pass a ticking `nowMs` (useNow hook) — idle subtrees
  // no longer re-render since the token-refresh render-storm fix, so labels
  // computed from Date.now() at render time freeze on an idle tab.
  const now = nowMs ?? Date.now();
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return units.now;
  if (diffMin < 60) return `${diffMin}${units.m}`;
  if (diffHour < 24) return `${diffHour}${units.h}`;
  if (diffDay < 7) return `${diffDay}${units.d}`;

  return formatDateShort(date);
}

/**
 * Debounce function - delays execution until after wait milliseconds have elapsed
 * since the last time the function was invoked
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}
